<template lang="pug">
.user-select
  .mb-4(v-if="multiple && model && model.length > 0")
    UsersField(
      :user-ids="model",
      show-avatar,
      type="stack",
      closable,
      @close="removeUser"
    )
  el-select(
    v-model="model",
    v-cancel-read-only,
    filterable,
    remote,
    :remote-method="filter",
    :multiple="props.multiple",
    :placeholder="props.placeholder",
    placement="bottom",
    suffix-icon="ArrowDown",
    :remote-show-suffix="true",
    :disabled="props.disabled",
    clearable
  )
    template(#prefix)
      font-awesome-icon(:icon="['fas', 'fa-magnifying-glass']")
    el-option-group(v-lazy-load="loadMoreData")
      el-option(
        v-for="item in result",
        :key="item.id",
        :value="item.id",
        :label="`${item.lastName}, ${item.firstName} (${item.username})`"
      )
      el-option(v-if="loading", v-loading="true", :value="''", label="") 
    template(v-if="multiple", #tag)
      span
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed } from "vue";
import type { PaginationQuery, Pagination } from "../../../services/common";
import {
  type UserSearchQuery,
  type KeyCloakUser,
  searchUsersInfos,
} from "../../../services/userManagementKeyCloak";
import { debounce } from "lodash";
import { useUserInformationStore } from "../../../stores/userInformation";
import { stateInitial } from "@shared/models/state";
import UsersField from "@shared/components/twinManagement/components/UsersField.vue";
import { cancelReadOnly as vCancelReadOnly } from "@shared/directives/select-readonly";
import vLazyLoad from "@shared/directives/elSelectInfiniteScroll";

const result = ref<KeyCloakUser[]>([]);
const page = ref<Pagination>(null);

// NOTE: most data loading is happening in this component itself, since we need to support infinite scrolling and search across an arbitrary number of input field that are being rendered to the user
onMounted(() => {
  loadSelectedUsers();
  loadMoreData();
  // if (props.modelValue) {
  //   result.value = props.modelValue
  // }
});

const props = defineProps<{
  modelValue?: Array<string> | null;
  multiple?: boolean;
  placeholder?: string;
  disabled?: boolean;
  clearable?: boolean;
  lazyLoad?: boolean;
}>();

const emit = defineEmits(["update:modelValue"]);

const pagination = ref<PaginationQuery>({
  page: 0,
  pageSize: 102,
});

const loading = ref(false);
const loaded = ref(false);
const error = ref(false);

const loadSearch = ref();

let abortController: AbortController | null = null;

const userInformationStore = useUserInformationStore();

function loadSelectedUsers() {
  if (props.modelValue && props.modelValue.length > 0) {
    userInformationStore.loadUserInformation(props.modelValue);
  }
}

const selectedUsers = computed(() => {
  if (!Array.isArray(props.modelValue)) {
    return [];
  }
  return props.modelValue
    .map(
      (id) =>
        userInformationStore.userInformationCache.get(id) ?? {
          ...stateInitial,
        },
    )
    .filter((item) => item?.loaded)
    .map((item) => item?.result);
});

async function loadMoreData() {
  // NOTE: if we already load the current search query, do not reload
  if (
    search.value === loadSearch.value &&
    (loading.value || (loaded.value && !hasMorePages.value))
  ) {
    return;
  }
  // NOTE: search queries might take time depending on complexity, we have to make sure that we actually only retrieve the last request, thus prior requests have to be cancelled
  if (abortController !== null) {
    try {
      abortController.abort();
    } catch (e) {
      // noop
    }
    abortController = null;
  }
  loadSearch.value = search.value;
  loaded.value = false;
  loading.value = true;
  pagination.value.page++;
  abortController = new AbortController();
  const response = await searchUsersInfos({
    ...pagination.value,
    search: loadSearch.value !== "" ? loadSearch.value : undefined,
  }).catch((e) => {
    if (e && e?.code === "ERR_CANCELED") {
      // We cancelled the request in favor of a new search query
      e.dontLog = true;
      throw e;
    }
    error.value = true;
    loading.value = false;
    loaded.value = true;
    throw e;
  });
  // NOTE: if we load the first page of a search, we want to override all previous data.
  // This is important because a request might finish between being cancelled and a data reset by a new request, in which case its data will be in the result.
  // In case of a next page load, we have to merge the data and then filter for uniqueness in case of new data
  if (pagination.value.page === 1) {
    // NOTE: If there is no search, we want to add the already selected user data, so that the element can resolve the ids to names
    result.value = [...selectedUsers.value, ...response.data.result]
      // Filter for uniqueness, note that this isn't very effective but okay for 10 items
      .filter((item, pos, self) => {
        return self.findIndex((other) => item?.id === other?.id) == pos;
      });
  } else {
    result.value = [...result.value, ...response.data.result]
      // Filter for uniqueness, note that this isn't very effective but okay for human possible scroll sizes, this makes sense since there might be items created while scrolling, resulting in differences in paging
      .filter((item, pos, self) => {
        return self.findIndex((other) => item?.id === other?.id) == pos;
      });
  }
  page.value = {
    total: response.data.total,
    pagesTotal: response.data.pagesTotal,
  };
  loading.value = false;
  loaded.value = true;
}

const debouncedLoadData = debounce(loadMoreData, 300);

const search = ref<string>("");

watch(search, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    if (newValue !== "") {
      pagination.value.page = 0;
      result.value = [];
      if (page.value !== null) {
        page.value.pagesTotal = null;
      }
      loading.value = true;
      debouncedLoadData();
    } else {
      pagination.value.page = 0;
      result.value = [];
      if (page.value !== null) {
        page.value.pagesTotal = null;
      }
      loading.value = true;
      loadMoreData();
    }
  }
});

function filter(input: string) {
  search.value = input;
}

const hasMorePages = computed(() => {
  if (page.value?.pagesTotal === null) {
    // inital load
    return true;
  }
  if (page.value !== null) {
    return page.value.pagesTotal > pagination.value.page;
  }
  return false;
});

const model = computed({
  get() {
    if (props.multiple) {
      return props.modelValue;
    } else {
      return props.modelValue && props.modelValue.length > 0
        ? props.modelValue[0]
        : null;
    }
  },
  set(value) {
    if (Array.isArray(value)) {
      emit("update:modelValue", value);
    } else {
      emit(
        "update:modelValue",
        value !== null && value !== undefined ? [value] : [],
      );
    }
  },
});

function removeUser(userId: string) {
  if (Array.isArray(model.value)) {
    model.value = model.value.filter((item) => item !== userId);
  } else {
    model.value = null;
  }
}
</script>
